import { forwardRef, useEffect, useState } from "react";

import { isNotNil } from "emoreg/utils";

import { Input, InputProps } from "../input";

export type NumberInputProps = Omit<InputProps, "type" | "value" | "onChangeValue"> & {
  value?: number | null;
  onChangeValue?: (value: number | null) => void;
  maxDecimals?: number;
};

const formatNumberValue = (value: string) => {
  if (value === "" || value === "-") return null;
  // return numeric value
  return Number(value.replace(",", "."));
};

const removeAdditionalDecimalPoints = (value: string) => {
  const [left, right] = value.split(/\.|\,/);
  if (!isNotNil(right)) return left;
  return `${left},${right.replace(/[^0-9]/g, "")}`;
};

const restrictDecimals = (value: string, maxDecimals?: number) => {
  if (!isNotNil(maxDecimals)) return value;
  const [left, right] = value.split(/\.|\,/);
  if (!isNotNil(right)) return left;
  return `${left},${right.replace(/[^0-9]/g, "").substring(0, maxDecimals)}`;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChangeValue, maxDecimals, ...props }, ref) => {
    const [localValue, setLocalValue] = useState(() =>
      // restrict decimals and convert dot to comma (standard european format)
      isNotNil(value) ? restrictDecimals(String(value).replace(".", ","), maxDecimals) : ""
    );

    const handleChangeValue = (value: string) => {
      // preserve leading minus sign
      const isNegative = value.startsWith("-");
      // if value starts with a separator remove it (after optional minus)
      value = value.replace(/^-?(\.|\,)+/, isNegative ? "-" : "");
      // remove all non-numeric characters (keep leading minus)
      value = value.replace(isNegative ? /(?!^-)[^0-9.,]/g : /[^0-9.,]/g, "");
      // remove more than one decimal point
      value = removeAdditionalDecimalPoints(value);
      // restrict decimals
      value = restrictDecimals(value, maxDecimals);
      // emit clear value
      setLocalValue(value.replace(".", ","));
      // emit value to parent
      onChangeValue?.(formatNumberValue(value));
    };

    useEffect(() => {
      // if value changes, update local value
      const localNumberValue = formatNumberValue(localValue);
      // if local and parent value are different, update local value
      if (value !== localNumberValue)
        setLocalValue(
          isNotNil(value)
            ? // restrict decimals and convert dot to comma (standard european format)
              restrictDecimals(String(value).replace(".", ","), maxDecimals)
            : ""
        );
    }, [value]);

    return (
      <Input
        ref={ref}
        type="text"
        value={localValue}
        onChangeValue={handleChangeValue}
        {...props}
      />
    );
  }
);

NumberInput.displayName = "NumberInput";
